import * as z from "zod";

const timePeriodSchema = z.enum([
  "day",
  "week",
  "month",
  "last-month",
  "quarter",
  "all",
  "custom",
]);

const customRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

// Lightweight export row schema
export const exportOnboardingRowSchema = z.object({
  id: z.string(),
  merchantId: z.string(),
  name: z.string(),
  type: z.enum(["Self-Serve", "Custom"]),
  startDateTime: z.string(),
  lastSessionAt: z.string(),
  dealSize: z.number().nullable(),
  onboardingProgress: z.enum([
    "Completed",
    "Reopened",
    "Awaiting approval",
    "In progress",
    "Partial Completion",
    "Help Requested",
  ]),
  pushConfigurationDate: z.string().nullable(),
  durationSeconds: z.number().nullable(),
  totalSessions: z.number().nullable(),
  completionPercent: z.number().nullable(),
  weightedRevenue: z.number().nullable(),
  weightPercent: z.number().nullable(),
  product: z.enum(["returns", "order-editing"]).optional(),
});

export const exportSelfServeOnboardings = {
  name: "exportSelfServeOnboardings" as const,
  input: z
    .object({
      timePeriod: timePeriodSchema,
      customRange: customRangeSchema.optional(),
      product: z.enum(["returns", "order-editing"]).optional(),
      statuses: z
        .array(
          z.enum([
            "Completed",
            "Reopened",
            "Awaiting approval",
            "In progress",
            "Partial Completion",
            "Help Requested",
          ]),
        )
        .optional(),
      searchQuery: z.string().optional(),
      configurationPushedFilter: z.enum(["yes", "no"]).optional(),
      startDateRange: z
        .object({ start: z.string().min(1), end: z.string().min(1) })
        .optional(),
      durationFilter: z
        .object({ operator: z.enum(["lt", "gt"]), value: z.number().min(0) })
        .optional(),
      dealSizeFilter: z
        .object({
          operator: z.enum(["lt", "gt", "between"]),
          value: z.number().min(0),
          maxValue: z.number().min(0).optional(),
        })
        .optional(),
      filterLowIntent: z.boolean().optional(),
      typeFilter: z.enum(["all", "Self-Serve", "Custom"]).optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timePeriod === "custom" && !value.customRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is custom",
          path: ["customRange"],
        });
      }
    }),
  output: z.object({
    onboardings: z.array(exportOnboardingRowSchema),
    total: z.number(),
  }),
};

export type ExportOnboardingRow = z.infer<typeof exportOnboardingRowSchema>;
export type ExportSelfServeOnboardingsInput = z.infer<
  typeof exportSelfServeOnboardings.input
>;
export type ExportSelfServeOnboardingsOutput = z.infer<
  typeof exportSelfServeOnboardings.output
>;
